import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Avatar,
  Button,
  Image,
  List,
  Mentions,
  Popconfirm,
  Space,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BoldOutlined,
  CloseCircleFilled,
  CodeOutlined,
  DeleteOutlined,
  EditOutlined,
  PictureOutlined,
  SendOutlined,
  SmileOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import type { GetRef } from 'antd';
import type { Comment, SpaceUserGroup } from '@lens/shared';
import { listItemComments, createItemComment, updateComment, deleteComment } from './api.ts';
import { ReactionBar } from '../reactions/ReactionBar.tsx';
import { ReactionPicker } from '../reactions/ReactionPicker.tsx';
import { addCommentReaction, removeCommentReaction } from '../reactions/api.ts';
import {
  buildMentionIndex,
  extractMentions as extractMentionsFromText,
  groupMentionToken,
  MENTION_ALL_TOKEN,
  renderCommentBody,
} from './mentions.tsx';
import { listSpaceUserGroups } from '../spaces/api.ts';
import { readCommentDraft, writeCommentDraft, type PendingImage } from './draft.ts';
import { apiRequest } from '../../shared/api/client.ts';
import { uploadAttachment } from '../../shared/api/attachments.ts';
import { radius, spacing } from '../../shared/theme/tokens.ts';
import styles from './CommentSection.module.css';

const { Text } = Typography;

/** localStorage-ключ порядка сортировки комментариев (общий для всех записей). */
const COMMENT_SORT_KEY = 'lens.comments-sort';

/** Палитра фонов аватаров-инициалов (brand-производные, детерминированно по имени). */
const AVATAR_COLORS = ['#6C5CE7', '#27AE60', '#2980B9', '#F39C12', '#E74C3C', '#0D9488'] as const;

/** Инициалы из displayName: первые буквы 1-2 слов, uppercase. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.slice(0, 2).map((w) => w[0] ?? '');
  return letters.join('').toUpperCase() || '?';
}

/** Время комментария — без секунд: строка шапки коротка, и «16:51:17»
    переносило дату на вторую строку. Полная дата с секундами остаётся в
    подсказке при наведении. */
function formatCommentTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Детерминированный фон аватара по строке (без новых данных). */
function avatarColorOf(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx] ?? AVATAR_COLORS[0];
}

interface SpaceMember {
  userId: string;
  login: string;
  displayName: string;
  role: string;
}

interface CommentSectionProps {
  spaceId: string;
  itemId: string;
  currentUserId: string;
  /** Наблюдатель (canEdit=false) видит ленту, но не композер и не действия над своими комментариями. */
  canEdit?: boolean;
  /** Контент слева в строке-заголовке ленты (напр. кнопка «Саммари»); справа —
      переключатель порядка. Оба живут в одной строке, чтобы не тратить лишний ряд. */
  headerLeft?: ReactNode;
}

export function CommentSection(props: CommentSectionProps): JSX.Element {
  // Remount на смену записи: черновик, комментарии и локальное состояние
  // композера всегда привязаны к конкретной записи.
  return <CommentSectionInner key={props.itemId} {...props} />;
}

function CommentSectionInner({ spaceId, itemId, currentUserId, canEdit = true, headerLeft }: CommentSectionProps): JSX.Element {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState(() => readCommentDraft(itemId)?.body ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  // Ответ: поле открыто под веткой rootId, отвечаем на commentId (это может
  // быть и ответ внутри ветки — сервер прицепит его к первому комментарию).
  const [replyTarget, setReplyTarget] = useState<{ rootId: string; commentId: string } | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [userGroups, setUserGroups] = useState<SpaceUserGroup[]>([]);
  // Порядок списка: false = сначала старые (как в переписке), true = сначала
  // новые. Выбор общий для всех записей, живёт в localStorage.
  const [sortDesc, setSortDesc] = useState(() => {
    try {
      return localStorage.getItem(COMMENT_SORT_KEY) === 'desc';
    } catch {
      return false;
    }
  });

  const toggleSort = () => {
    setSortDesc((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COMMENT_SORT_KEY, next ? 'desc' : 'asc');
      } catch {
        // localStorage недоступен — порядок просто не запомнится
      }
      return next;
    });
  };

  // Лента — комментарии верхнего уровня; ответы идут под своим комментарием,
  // всегда сначала старые, как в переписке. Ответ, чей первый комментарий
  // не пришёл в список, показываем в ленте как обычный.
  const { rootComments, repliesByRoot } = useMemo(() => {
    const ids = new Set(comments.map((c) => c.id));
    const roots: Comment[] = [];
    const byRoot = new Map<string, Comment[]>();
    for (const c of comments) {
      if (c.parentId && ids.has(c.parentId)) {
        byRoot.set(c.parentId, [...(byRoot.get(c.parentId) ?? []), c]);
      } else {
        roots.push(c);
      }
    }
    return { rootComments: sortDesc ? roots.reverse() : roots, repliesByRoot: byRoot };
  }, [comments, sortDesc]);

  const load = useCallback(async () => {
    try {
      const res = await listItemComments(spaceId, itemId);
      setComments(res.comments);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [spaceId, itemId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Load space members for mention autocomplete
  useEffect(() => {
    apiRequest<{ members: SpaceMember[] }>(`/api/spaces/${spaceId}/members?limit=100`)
      .then((res) => setMembers(res.members ?? []))
      .catch(() => {});
  }, [spaceId]);

  // Группы пространства — их тоже можно упомянуть («@Команда_Android»), как и
  // всех разом («@все»). Кого именно позвать, сервер решит при отправке.
  useEffect(() => {
    listSpaceUserGroups(spaceId)
      .then((res) => setUserGroups(res.groups ?? []))
      .catch(() => {});
  }, [spaceId]);

  const mentionIndex = useMemo(
    () =>
      buildMentionIndex(
        members,
        userGroups.map((g) => ({ id: g.id, name: g.name, memberCount: g.members.length })),
      ),
    [members, userGroups],
  );

  const extractMentions = useCallback(
    (text: string) => extractMentionsFromText(text, mentionIndex),
    [mentionIndex],
  );

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Прикреплённые к черновику картинки — показываются миниатюрами под полем,
  // а markdown `![имя](url)` подмешивается в тело только при отправке (поле
  // ввода не засоряется длинными URL).
  const [pendingImages, setPendingImages] = useState<PendingImage[]>(
    () => readCommentDraft(itemId)?.images ?? [],
  );

  // Черновик переживает закрытие дравера и переключение вкладок: текст и
  // прикреплённые картинки сохраняются per-item и восстанавливаются при
  // повторном открытии (пустой черновик — удаляется).
  useEffect(() => {
    writeCommentDraft(itemId, { body, images: pendingImages });
  }, [itemId, body, pendingImages]);

  /** Загружает файлы-картинки как вложения и добавляет их в превью черновика. */
  const uploadToPending = useCallback(
    async (files: File[]) => {
      const images = files.filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) return;
      setUploading(true);
      const key = 'comment-image-upload';
      message.loading({ content: 'Загрузка изображения…', key, duration: 0 });
      try {
        for (const file of images) {
          const att = await uploadAttachment(spaceId, file);
          setPendingImages((prev) => [...prev, { url: att.url, fileName: att.fileName }]);
        }
        message.destroy(key);
      } catch {
        message.error({ content: 'Не удалось загрузить изображение', key });
      } finally {
        setUploading(false);
      }
    },
    [spaceId],
  );

  /** onPaste/onDrop для поля нового комментария: картинки → превью-черновик. */
  const imageDnd = useMemo(
    () => ({
      onPaste: (e: React.ClipboardEvent) => {
        const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
        if (files.length > 0) {
          e.preventDefault();
          void uploadToPending(files);
        }
      },
      onDrop: (e: React.DragEvent) => {
        const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith('image/'));
        if (files.length > 0) {
          e.preventDefault();
          void uploadToPending(files);
        }
      },
    }),
    [uploadToPending],
  );

  const mentionsRef = useRef<GetRef<typeof Mentions>>(null);
  const [composerFocused, setComposerFocused] = useState(false);
  // Композер «активен» — раскрыт с кнопками — когда в нём фокус, есть
  // текст или прикреплённые картинки. Иначе это одна строка-приглашение.
  const composerActive = composerFocused || body.trim().length > 0 || pendingImages.length > 0;
  /** Есть что отправить — только тогда показываем кнопку отправки. */
  const canSend = body.trim().length > 0 || pendingImages.length > 0;
  // Выделение, которое нужно восстановить после того, как React закоммитит
  // новое value в textarea (rAF для этого ненадёжен — фоновые вкладки).
  const pendingSelectionRef = useRef<{ start: number; end: number } | null>(null);

  useLayoutEffect(() => {
    const sel = pendingSelectionRef.current;
    if (!sel) return;
    pendingSelectionRef.current = null;
    const el = mentionsRef.current?.textarea;
    if (!el) return;
    el.focus();
    el.setSelectionRange(sel.start, sel.end);
  }, [body]);

  /**
   * Оборачивает выделение в markdown-маркер (`**` / `` ` ``) или снимает его,
   * если выделение уже обёрнуто. Курсор/выделение сохраняются.
   */
  const applyMarker = useCallback(
    (marker: string) => {
      const ta = mentionsRef.current?.textarea;
      const start = ta?.selectionStart ?? body.length;
      const end = ta?.selectionEnd ?? body.length;
      const before = body.slice(0, start);
      const selected = body.slice(start, end);
      const after = body.slice(end);
      const mLen = marker.length;

      let next: string;
      let selStart: number;
      let selEnd: number;
      if (before.endsWith(marker) && after.startsWith(marker)) {
        next = before.slice(0, -mLen) + selected + after.slice(mLen);
        selStart = start - mLen;
        selEnd = end - mLen;
      } else {
        next = before + marker + selected + marker + after;
        selStart = start + mLen;
        selEnd = end + mLen;
      }
      pendingSelectionRef.current = { start: selStart, end: selEnd };
      setBody(next);
    },
    [body],
  );

  const handleSubmit = async () => {
    const text = body.trim();
    const imagesMd = pendingImages.map((img) => `![${img.fileName}](${img.url})`).join('\n');
    const finalBody = [text, imagesMd].filter(Boolean).join('\n\n');
    if (!finalBody) return;
    setSubmitting(true);
    try {
      const mentions = extractMentions(finalBody);
      const comment = await createItemComment(spaceId, itemId, { body: finalBody, mentions });
      setComments((prev) => [...prev, comment]);
      setBody('');
      setPendingImages([]);
    } catch {
      void message.error('Не удалось отправить комментарий');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (commentId: string) => {
    if (!editBody.trim()) return;
    try {
      const mentions = extractMentions(editBody);
      const updated = await updateComment(spaceId, commentId, { body: editBody, mentions });
      setComments((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
      setEditingId(null);
    } catch {
      void message.error('Не удалось обновить комментарий');
    }
  };

  const handleReply = async () => {
    if (!replyTarget || !replyBody.trim()) return;
    setReplySubmitting(true);
    try {
      const text = replyBody.trim();
      const comment = await createItemComment(spaceId, itemId, {
        body: text,
        mentions: extractMentions(text),
        parentId: replyTarget.commentId,
      });
      setComments((prev) => [...prev, comment]);
      setReplyTarget(null);
      setReplyBody('');
    } catch {
      void message.error('Не удалось отправить ответ');
    } finally {
      setReplySubmitting(false);
    }
  };

  const startReply = (rootId: string, commentId: string) => {
    setReplyTarget({ rootId, commentId });
    setReplyBody('');
  };

  const handleDelete = async (commentId: string) => {
    try {
      await deleteComment(spaceId, commentId);
      // Как на сервере: ответы удалённого комментария остаются в ленте.
      setComments((prev) =>
        prev
          .filter((c) => c.id !== commentId)
          .map((c) => (c.parentId === commentId ? { ...c, parentId: null } : c)),
      );
    } catch {
      void message.error('Не удалось удалить комментарий');
    }
  };

  const handleToggleReaction = useCallback(
    async (commentId: string, emoji: string) => {
      const comment = comments.find((c) => c.id === commentId);
      if (!comment) return;
      const active = comment.reactions.some((r) => r.emoji === emoji && r.reactedByMe);
      try {
        const res = active
          ? await removeCommentReaction(spaceId, commentId, emoji)
          : await addCommentReaction(spaceId, commentId, emoji);
        setComments((prev) =>
          prev.map((c) => (c.id === commentId ? { ...c, reactions: res.reactions } : c)),
        );
      } catch (err) {
        void message.error(err instanceof Error ? err.message : 'Не удалось изменить реакцию');
      }
    },
    [comments, spaceId],
  );

  // Порядок подсказок: сначала «все», потом группы, потом люди — короткий
  // путь позвать команду не тонет в списке из сотни участников.
  const mentionOptions = [
    { value: MENTION_ALL_TOKEN, label: `все · участники пространства (${members.length})` },
    ...userGroups
      .filter((g) => g.members.length > 0)
      .map((g) => ({
        value: groupMentionToken(g.name),
        label: `${g.name} · группа (${g.members.length})`,
      })),
    ...members.map((m) => ({
      value: m.login,
      label: `${m.displayName} (${m.login})`,
    })),
  ];

  /** Один комментарий — верхнего уровня или ответ (rootId — первый комментарий ветки). */
  const renderComment = (comment: Comment, rootId: string, isReply: boolean): JSX.Element => {
    const isOwn = comment.authorId === currentUserId;
    const editing = editingId === comment.id;
    return (
      <div className={styles.row} data-testid={`comment-${comment.id}`}>
        <Avatar
          size={isReply ? 22 : 26}
          style={{
            backgroundColor: avatarColorOf(comment.author.displayName),
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: isReply ? 10 : 11,
            flex: '0 0 auto',
            marginTop: 1,
          }}
        >
          {initialsOf(comment.author.displayName)}
        </Avatar>
        <div className={styles.main}>
          <div className={styles.head}>
            <Text strong className={styles.author} style={{ fontSize: 13, lineHeight: 1.2 }}>
              {comment.author.displayName}
            </Text>
            <Text
              type="secondary"
              className={styles.time}
              style={{ fontSize: 11, lineHeight: 1 }}
              title={new Date(comment.createdAt).toLocaleString('ru-RU')}
            >
              {formatCommentTime(comment.createdAt)}
            </Text>
            {canEdit && !editing && (
              <span className={styles.actions}>
                <ReactionPicker
                  reactions={comment.reactions}
                  onToggle={(emoji) => void handleToggleReaction(comment.id, emoji)}
                  placement="topRight"
                >
                  <Tooltip title="Реакция">
                    <Button
                      type="text"
                      size="small"
                      className={styles.actionBtn}
                      icon={<SmileOutlined />}
                      data-testid={`comment-react-${comment.id}`}
                    />
                  </Tooltip>
                </ReactionPicker>
                {isOwn && (
                  <>
                    <Button
                      type="text"
                      size="small"
                      className={styles.actionBtn}
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditBody(comment.body);
                      }}
                    />
                    <Popconfirm
                      title="Удалить комментарий?"
                      okText="Удалить"
                      cancelText="Отмена"
                      onConfirm={() => void handleDelete(comment.id)}
                    >
                      <Button type="text" size="small" danger className={styles.actionBtn} icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </>
                )}
              </span>
            )}
          </div>
          {editing ? (
            <Space.Compact style={{ width: '100%', marginTop: 2 }}>
              <Mentions
                value={editBody}
                onChange={setEditBody}
                autoSize={{ minRows: 1, maxRows: 4 }}
                options={mentionOptions}
                filterOption={(input, opt) =>
                  String((opt as { label?: unknown })?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleEdit(comment.id);
                  }
                  if (e.key === 'Escape') {
                    // stopPropagation: Escape не должен долетать до Drawer и закрывать его
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingId(null);
                  }
                }}
                autoFocus
                style={{ flex: 1 }}
              />
              <Button type="primary" onClick={() => void handleEdit(comment.id)}>
                Сохранить
              </Button>
              <Button onClick={() => setEditingId(null)}>Отмена</Button>
            </Space.Compact>
          ) : (
            <div className={styles.body}>
              {renderCommentBody(comment.body, mentionIndex)}
            </div>
          )}
          {!editing && comment.reactions.some((r) => r.count > 0) && (
            <div className={styles.reactions}>
              <ReactionBar
                reactions={comment.reactions}
                canReact={canEdit}
                size="sm"
                showAdd={false}
                onToggle={(emoji) => void handleToggleReaction(comment.id, emoji)}
              />
            </div>
          )}
          {canEdit && !editing && (
            <Button
              type="link"
              size="small"
              className={styles.replyLink}
              onClick={() => startReply(rootId, comment.id)}
              data-testid={`comment-reply-${comment.id}`}
            >
              Ответить
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="comment-section" className={styles.root}>
      {(headerLeft || comments.length > 1) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>{headerLeft}</div>
          {comments.length > 1 && (
            <Button
              type="text"
              size="small"
              icon={sortDesc ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
              onClick={toggleSort}
              style={{ color: 'var(--color-text-secondary)', fontSize: 12, flex: '0 0 auto' }}
              data-testid="comment-sort-toggle"
            >
              {sortDesc ? 'Сначала новые' : 'Сначала старые'}
            </Button>
          )}
        </div>
      )}
      <List
        loading={loading}
        dataSource={rootComments}
        locale={{
          // [density] Пустая лента не резервирует пол-экрана под надпись:
          // композер поднимается сразу под вкладки, писать можно не целясь
          // мимо пустоты.
          // Пустой ленте надпись не нужна: композер под вкладкой и так
          // говорит, что комментариев пока нет. Наблюдателю (без композера)
          // оставляем короткое слово, чтобы вкладка не была пустой.
          emptyText: canEdit ? (
            <span data-testid="comments-empty" />
          ) : (
            <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Нет комментариев</span>
          ),
        }}
        renderItem={(comment) => {
          const replies = repliesByRoot.get(comment.id) ?? [];
          return (
            <List.Item key={comment.id}>
              <div className={styles.thread}>
                {renderComment(comment, comment.id, false)}
                {(replies.length > 0 || replyTarget?.rootId === comment.id) && (
                  <div className={styles.replies} data-testid={`comment-replies-${comment.id}`}>
                    {replies.map((reply) => (
                      <div key={reply.id}>{renderComment(reply, comment.id, true)}</div>
                    ))}
                    {replyTarget?.rootId === comment.id && (
                      <div className={styles.replyInput} data-testid="comment-reply-input">
                        <Mentions
                          value={replyBody}
                          onChange={setReplyBody}
                          autoSize={{ minRows: 1, maxRows: 6 }}
                          placeholder="Ответ… @ — упоминание"
                          options={mentionOptions}
                          filterOption={(input, opt) =>
                            String((opt as { label?: unknown })?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              void handleReply();
                            }
                            if (e.key === 'Escape') {
                              // Escape закрывает поле ответа, а не карточку.
                              e.preventDefault();
                              e.stopPropagation();
                              setReplyTarget(null);
                            }
                          }}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                        <div className={styles.replyButtons}>
                          <Button size="small" onClick={() => setReplyTarget(null)}>
                            Отмена
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            loading={replySubmitting}
                            disabled={!replyBody.trim()}
                            onClick={() => void handleReply()}
                            data-testid="comment-reply-submit"
                          >
                            Ответить
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </List.Item>
          );
        }}
      />

      {/* Sticky-обёртка прибивает композер к низу зоны вкладки (см. .composer
          в CSS); сам композер — рамка с подсветкой фокуса: без неё было непонятно,
          куда вводить текст.
          Наблюдателю (canEdit=false) композер не показываем. */}
      {canEdit && (
      <div className={styles.composer}>
      <div
        data-testid="comment-input"
        onFocusCapture={() => setComposerFocused(true)}
        onBlurCapture={() => setComposerFocused(false)}
        style={{
          border: `1px solid ${composerFocused ? 'var(--color-primary)' : 'var(--color-border-strong)'}`,
          borderRadius: radius.md,
          background: 'var(--color-bg-container)',
          transition: 'border-color 0.2s',
        }}
      >
        <Mentions
          ref={mentionsRef}
          // Без autoFocus: композер забирал фокус при каждом открытии карточки
          // и при каждом переходе к соседней записи, из-за чего листание с
          // клавиатуры (стрелки, J/K) не работало вообще — клавиши уходили в
          // текст комментария. Открывают карточку обычно чтобы прочитать, а
          // не сразу написать; для комментария поле в одном клике.
          variant="borderless"
          style={{ width: '100%' }}
          value={body}
          onChange={setBody}
          placeholder="Напишите комментарий… @ — упоминание"
          // Одна строка, пока в поле не зашли: три строки с кнопками
          // форматирования на пустой ленте весили больше самой записи.
          autoSize={{ minRows: composerActive ? 2 : 1, maxRows: 10 }}
          options={mentionOptions}
          filterOption={(input, opt) =>
            String((opt as { label?: unknown })?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
              e.preventDefault();
              applyMarker('**');
              return;
            }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'e') {
              e.preventDefault();
              applyMarker('`');
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSubmit();
            }
            if (e.key === 'Escape') {
              // Как и при правке существующего комментария: Escape не должен
              // долетать до карточки записи и закрывать её посреди набора.
              e.stopPropagation();
            }
          }}
          {...imageDnd}
        />

        {pendingImages.length > 0 && (
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: `0 ${spacing.sm}px ${spacing.xs}px` }}
            data-testid="comment-pending-images"
          >
            {pendingImages.map((img, idx) => (
              <div key={`${img.url}-${idx}`} style={{ position: 'relative', lineHeight: 0 }}>
                <Image
                  src={img.url}
                  alt={img.fileName}
                  width={72}
                  height={72}
                  style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid var(--color-border)' }}
                  preview={{ mask: 'Открыть' }}
                />
                <Tooltip title="Убрать">
                  <CloseCircleFilled
                    onClick={() => setPendingImages((prev) => prev.filter((_, i) => i !== idx))}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      fontSize: 18,
                      color: 'var(--color-text-secondary)',
                      background: 'var(--color-bg-container)',
                      borderRadius: '50%',
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        )}

        {composerActive && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            padding: spacing.xs,
          }}
          data-testid="comment-toolbar"
        >
          <Tooltip title="Жирный (Ctrl+B)">
            <Button
              type="text"
              size="small"
              icon={<BoldOutlined />}
              // preventDefault: не отдавать фокус из textarea, иначе теряется выделение
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyMarker('**')}
              data-testid="comment-format-bold"
            />
          </Tooltip>
          <Tooltip title="Код (Ctrl+E)">
            <Button
              type="text"
              size="small"
              icon={<CodeOutlined />}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyMarker('`')}
              data-testid="comment-format-code"
            />
          </Tooltip>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) void uploadToPending(files);
              e.target.value = '';
            }}
          />
          <Tooltip title="Прикрепить изображение (или Ctrl+V / перетащить)">
            <Button
              type="text"
              size="small"
              icon={<PictureOutlined />}
              loading={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid="comment-attach-image"
            />
          </Tooltip>
          {/* Отправка — одна иконка, и только когда есть что отправить.
              Широкая кнопка «Отправить» стояла всегда и большую часть
              времени была выключенной: занимала место и читалась как
              неработающая. Пока поле пустое, на её месте подсказка про
              Enter, которая и есть основной способ отправки. */}
          {canSend ? (
            <Button
              type="primary"
              className={styles.sendBtn}
              icon={<SendOutlined />}
              loading={submitting}
              disabled={uploading}
              onClick={() => void handleSubmit()}
              data-testid="comment-submit"
            >
              Отправить
            </Button>
          ) : (
            <Text type="secondary" className={styles.sendHint}>
              Enter — отправить · Shift+Enter — перенос
            </Text>
          )}
        </div>
        )}
      </div>
      </div>
      )}
    </div>
  );
}
